import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot, Root } from "react-dom/client";
import CpgPaymentReceipt from "./CpgPaymentReceipt";
import { CpgPaymentReceiptData } from "./types";

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const buildFileName = (data: CpgPaymentReceiptData): string =>
  `tylt-receipt-${data.fileId || "receipt"}.pdf`;

export const downloadCpgPaymentReceiptPdf = async (
  data: CpgPaymentReceiptData,
): Promise<void> => {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.width = "560px";
  host.style.padding = "0";
  host.style.margin = "0";
  host.style.background = "#ffffff";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);

  let root: Root | null = createRoot(host);

  try {
    root.render(<CpgPaymentReceipt data={data} />);
    await waitForNextPaint();
    await new Promise((resolve) => setTimeout(resolve, 80));

    const receiptEl = host.querySelector(
      "#cpg-payment-receipt",
    ) as HTMLElement | null;

    if (!receiptEl) {
      throw new Error("Receipt failed to render");
    }

    const canvas = await html2canvas(receiptEl, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      foreignObjectRendering: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;

    const widthRatio = maxWidth / canvas.width;
    const heightRatio = maxHeight / canvas.height;
    const ratio = Math.min(widthRatio, heightRatio, 1);

    const renderWidth = canvas.width * ratio;
    const renderHeight = canvas.height * ratio;
    const x = (pageWidth - renderWidth) / 2;
    const y = margin;

    const explorerLink = receiptEl.querySelector(
      "#cpg-receipt-explorer-link",
    ) as HTMLAnchorElement | null;

    const addExplorerLink = (
      imageX: number,
      imageY: number,
      imageWidth: number,
      imageHeight: number,
    ) => {
      const url = explorerLink?.href;
      if (!explorerLink || !url) return;

      const receiptRect = receiptEl.getBoundingClientRect();
      const linkRect = explorerLink.getBoundingClientRect();
      if (!receiptRect.width || !receiptRect.height) return;

      const relX = (linkRect.left - receiptRect.left) / receiptRect.width;
      const relY = (linkRect.top - receiptRect.top) / receiptRect.height;
      const relW = linkRect.width / receiptRect.width;
      const relH = linkRect.height / receiptRect.height;
      const linkTop = relY * imageHeight;
      const pageIndex = Math.max(0, Math.floor(linkTop / maxHeight));
      const yOnPage = imageY + (linkTop - pageIndex * maxHeight);

      if (pageIndex > 0) {
        pdf.setPage(pageIndex + 1);
      }

      pdf.link(
        imageX + relX * imageWidth,
        yOnPage,
        relW * imageWidth,
        relH * imageHeight,
        { url },
      );
    };

    if (renderHeight <= maxHeight) {
      pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
      addExplorerLink(x, y, renderWidth, renderHeight);
    } else {
      const scaledWidth = maxWidth;
      const scaledHeight = (canvas.height * scaledWidth) / canvas.width;
      let heightLeft = scaledHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, scaledWidth, scaledHeight);
      heightLeft -= maxHeight;

      while (heightLeft > 0) {
        position = margin - (scaledHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          position,
          scaledWidth,
          scaledHeight,
        );
        heightLeft -= maxHeight;
      }
      addExplorerLink(margin, margin, scaledWidth, scaledHeight);
    }

    const internal = pdf.internal as typeof pdf.internal & {
      write: (...args: unknown[]) => unknown;
    };
    const originalWrite = internal.write;
    internal.write = (...args: unknown[]) => {
      if (
        args.length === 1 &&
        typeof args[0] === "string" &&
        args[0].includes("/Subtype /Link") &&
        args[0].includes("/S /URI")
      ) {
        args[0] = args[0].replace(
          "/A <</S /URI /URI (",
          "/A <</S /URI /NewWindow true /URI (",
        );
      }
      return originalWrite(...args);
    };

    pdf.save(buildFileName(data));
  } finally {
    root?.unmount();
    root = null;
    host.remove();
  }
};
